import { Box } from "@mantine/core";
import ChatMarkdown from "../ChatMarkdown";
import classes from "./XmlContentRenderer.module.css";

interface ParsedNode {
    type: "text" | "element";
    content?: string;
    tagName?: string;
    children?: ParsedNode[];
}

function parseXmlContent(content: string): ParsedNode[] {
    const nodes: ParsedNode[] = [];
    const stack: { tagName: string; children: ParsedNode[] }[] = [];
    const tagRegex = /<\/?[A-Z][A-Z0-9-]*>/g;
    let lastIndex = 0;
    let hasError = false;

    const pushText = (text: string) => {
        if (text.length === 0) {
            return;
        }
        const target = stack.length > 0 ? stack[stack.length - 1].children : nodes;
        target.push({ type: "text", content: text });
    };

    let match: RegExpExecArray | null = tagRegex.exec(content);
    while (match !== null) {
        const rawTag = match[0];
        const tagName = rawTag.replace(/[</>]/g, "");

        if (match.index > lastIndex) {
            pushText(content.slice(lastIndex, match.index));
        }

        if (rawTag.startsWith("</")) {
            const current = stack.pop();
            if (!current || current.tagName !== tagName) {
                hasError = true;
                break;
            }
            const target = stack.length > 0 ? stack[stack.length - 1].children : nodes;
            target.push({ type: "element", tagName, children: current.children });
        } else {
            stack.push({ tagName, children: [] });
        }

        lastIndex = tagRegex.lastIndex;
        match = tagRegex.exec(content);
    }

    if (hasError || stack.length > 0) {
        return [{ type: "text", content }];
    }

    if (lastIndex < content.length) {
        pushText(content.slice(lastIndex));
    }

    return nodes;
}

function formatTagName(tagName: string): string {
    // Convert DOCUMENT-CHUNK to Document Chunk
    return tagName
        .split("-")
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(" ");
}

interface NodeRendererProps {
    node: ParsedNode;
}

function NodeRenderer({ node }: NodeRendererProps) {
    if (node.type === "text") {
        return <ChatMarkdown content={node.content ?? ""} />;
    }

    const hasOnlyTextChildren = node.children?.length === 1 && node.children[0].type === "text";

    return (
        <fieldset className={classes.fieldset}>
            <legend className={classes.legend}>{formatTagName(node.tagName ?? "")}</legend>
            <Box className={classes.fieldsetContent}>
                {hasOnlyTextChildren ? (
                    <ChatMarkdown content={node.children?.[0].content ?? ""} />
                ) : (
                    node.children?.map((child, index) => <NodeRenderer key={index} node={child} />)
                )}
            </Box>
        </fieldset>
    );
}

interface XmlContentRendererProps {
    content: string;
}

function XmlContentRenderer({ content }: XmlContentRendererProps) {
    const nodes = parseXmlContent(content);

    // If no CAPITAL-CASE tags found, render as regular markdown
    if (nodes.length === 0 || (nodes.length === 1 && nodes[0].type === "text")) {
        return <ChatMarkdown content={content} />;
    }

    return (
        <Box>
            {nodes.map((node, index) => (
                <NodeRenderer key={index} node={node} />
            ))}
        </Box>
    );
}

export default XmlContentRenderer;
